// Auto-replies editor (PRD §4.4, Bot Manager → Auto Reply card). Three
// automations wired to tenants/{t}/config/automations and the backend inbound
// trigger: a welcome message, keyword rules, and an away / office-hours
// responder. Owner/admin save the whole config in one shot.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../lib/i18n';
import { cn } from '../lib/cn';
import Toggle from '../components/ui/Toggle';
import {
  useAutomations, saveAutomations, DEFAULT_AUTOMATIONS,
  type AutomationsConfig, type KeywordRule, type MatchMode,
} from '../lib/automations';

const inputCls = 'w-full rounded-[9px] border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary';

function Card({ title, desc, right, children }: { title: string; desc?: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-card border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <b className="block text-sm text-ink">{title}</b>
          {desc && <p className="mt-0.5 text-xs text-ink-3">{desc}</p>}
        </div>
        {right}
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function newRule(): KeywordRule {
  const id = (globalThis.crypto?.randomUUID?.() ?? `r_${Date.now()}_${Math.random().toString(36).slice(2)}`);
  return { id, keyword: '', match: 'contains', reply: '', enabled: true };
}

export default function AutoReplies() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { config, loading } = useAutomations();
  const [cfg, setCfg] = useState<AutomationsConfig>(DEFAULT_AUTOMATIONS);
  const [hydrated, setHydrated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && !hydrated) { setCfg(config); setHydrated(true); }
  }, [loading, hydrated, config]);

  function patchRule(id: string, patch: Partial<KeywordRule>) {
    setCfg((c) => ({ ...c, keywordRules: c.keywordRules.map((r) => (r.id === id ? { ...r, ...patch } : r)) }));
  }

  async function save() {
    setSaving(true); setErr(null); setSaved(false);
    try {
      // Drop empty keyword rules before saving.
      const clean = { ...cfg, keywordRules: cfg.keywordRules.filter((r) => r.keyword.trim() && r.reply.trim()) };
      await saveAutomations(clean);
      setCfg(clean);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1800);
    } catch (e) { setErr((e as Error).message); } finally { setSaving(false); }
  }

  if (loading || !hydrated) {
    return <div className="py-10 text-center text-sm text-ink-3">…</div>;
  }

  return (
    <div>
      <div className="mb-1 flex items-center gap-2 text-sm text-ink-3">
        <button type="button" onClick={() => navigate('/bots')} className="hover:text-ink">{t('bot.title')}</button>
        <span>/</span>
        <span className="font-semibold text-ink">{t('auto.title')}</span>
      </div>
      <h1 className="text-[22px] font-extrabold text-ink">{t('auto.title')}</h1>
      <p className="mb-5 text-ink-3">{t('auto.subtitle')}</p>

      <div className="space-y-5">
        {/* Welcome */}
        <Card
          title={t('auto.welcome.title')}
          desc={t('auto.welcome.desc')}
          right={<Toggle on={cfg.welcome.enabled} onChange={(v) => setCfg({ ...cfg, welcome: { ...cfg.welcome, enabled: v } })} label={t('auto.welcome.title')} />}
        >
          <textarea
            rows={3}
            value={cfg.welcome.message}
            onChange={(e) => setCfg({ ...cfg, welcome: { ...cfg.welcome, message: e.target.value } })}
            placeholder={t('auto.welcome.placeholder')}
            className={cn(inputCls, !cfg.welcome.enabled && 'opacity-50')}
          />
        </Card>

        {/* Keyword rules */}
        <Card title={t('auto.keyword.title')} desc={t('auto.keyword.desc')}>
          <div className="space-y-3">
            {cfg.keywordRules.length === 0 && (
              <p className="rounded-[9px] bg-surface px-3 py-4 text-center text-sm text-ink-3">{t('auto.keyword.empty')}</p>
            )}
            {cfg.keywordRules.map((r) => (
              <div key={r.id} className="rounded-[10px] border border-slate-200 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    value={r.keyword}
                    onChange={(e) => patchRule(r.id, { keyword: e.target.value })}
                    placeholder={t('auto.keyword.trigger')}
                    className={cn(inputCls, 'flex-1 min-w-[140px]')}
                  />
                  <select
                    value={r.match}
                    onChange={(e) => patchRule(r.id, { match: e.target.value as MatchMode })}
                    className={cn(inputCls, 'w-auto bg-white')}
                  >
                    <option value="contains">{t('auto.match.contains')}</option>
                    <option value="exact">{t('auto.match.exact')}</option>
                  </select>
                  <Toggle on={r.enabled} onChange={(v) => patchRule(r.id, { enabled: v })} label={t('auto.rule.enabled')} />
                  <button
                    type="button"
                    onClick={() => setCfg({ ...cfg, keywordRules: cfg.keywordRules.filter((x) => x.id !== r.id) })}
                    className="rounded-[8px] border border-red-500 px-3 py-1.5 text-xs font-bold text-red-500"
                  >
                    {t('auto.rule.delete')}
                  </button>
                </div>
                <textarea
                  rows={2}
                  value={r.reply}
                  onChange={(e) => patchRule(r.id, { reply: e.target.value })}
                  placeholder={t('auto.keyword.reply')}
                  className={cn(inputCls, 'mt-2')}
                />
              </div>
            ))}
            <button
              type="button"
              onClick={() => setCfg({ ...cfg, keywordRules: [...cfg.keywordRules, newRule()] })}
              className="rounded-[9px] border border-slate-200 px-4 py-2 text-[13px] font-bold text-ink"
            >
              + {t('auto.keyword.add')}
            </button>
          </div>
        </Card>

        {/* Away responder */}
        <Card
          title={t('auto.away.title')}
          desc={t('auto.away.desc')}
          right={<Toggle on={cfg.away.enabled} onChange={(v) => setCfg({ ...cfg, away: { ...cfg.away, enabled: v } })} label={t('auto.away.title')} />}
        >
          <div className={cn('space-y-4', !cfg.away.enabled && 'pointer-events-none opacity-50')}>
            <textarea
              rows={2}
              value={cfg.away.message}
              onChange={(e) => setCfg({ ...cfg, away: { ...cfg.away, message: e.target.value } })}
              placeholder={t('auto.away.placeholder')}
              className={inputCls}
            />
            <div className="flex flex-wrap items-end gap-4">
              <label className="block">
                <span className="mb-1 block text-xs font-bold text-ink-2">{t('auto.away.mode')}</span>
                <select
                  value={cfg.away.mode}
                  onChange={(e) => setCfg({ ...cfg, away: { ...cfg.away, mode: e.target.value as 'always' | 'schedule' } })}
                  className={cn(inputCls, 'bg-white')}
                >
                  <option value="schedule">{t('auto.away.schedule')}</option>
                  <option value="always">{t('auto.away.always')}</option>
                </select>
              </label>
              {cfg.away.mode === 'schedule' && (
                <>
                  <label className="block">
                    <span className="mb-1 block text-xs font-bold text-ink-2">{t('auto.away.open')}</span>
                    <input type="time" value={cfg.away.start} onChange={(e) => setCfg({ ...cfg, away: { ...cfg.away, start: e.target.value } })} className={inputCls} />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs font-bold text-ink-2">{t('auto.away.close')}</span>
                    <input type="time" value={cfg.away.end} onChange={(e) => setCfg({ ...cfg, away: { ...cfg.away, end: e.target.value } })} className={inputCls} />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs font-bold text-ink-2">{t('auto.away.offset')}</span>
                    <input
                      type="number" min={-12} max={14} value={cfg.away.utcOffset}
                      onChange={(e) => setCfg({ ...cfg, away: { ...cfg.away, utcOffset: Number(e.target.value) } })}
                      className={cn(inputCls, 'w-24')}
                    />
                  </label>
                </>
              )}
            </div>
          </div>
        </Card>

        {err && <div className="rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{err}</div>}

        <div className="sticky bottom-0 flex items-center gap-3 bg-gradient-to-t from-surface to-transparent py-3">
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving}
            className="rounded-[9px] bg-primary px-5 py-2.5 text-[13px] font-bold text-white disabled:opacity-40"
          >
            {saving ? '…' : saved ? t('auto.saved') : t('auto.save')}
          </button>
          <span className="text-xs text-ink-3">{t('auto.note')}</span>
        </div>
      </div>
    </div>
  );
}
